import {type HTMLAttributes, type RefCallback, useCallback, useEffect, useEffectEvent, useRef, useState} from 'react'

/**
 * How far short of reaching an element it still counts as reached.
 *
 * Zero would ask for the next page only once the reader has arrived at the end of what
 * is loaded, so every page would end in a wait. This asks while there is still something
 * left to scroll through.
 *
 * Both properties, because they buy the lead in different places and neither covers the
 * other. `rootMargin` expands the viewport, which is what a board sitting at the bottom
 * of a long page needs. `scrollMargin` expands the clip of every scroll container in
 * between, which is the one that matters most here: the board and each of its cells clip
 * their own sentinels, and `rootMargin` does nothing about either. Browsers without
 * `scrollMargin` ignore it and page at the moment the sentinel arrives, which is the
 * behaviour this is improving on rather than depending on.
 */
const REACHED_MARGIN = '200px'

/**
 * Calls `onReached` when the element the returned ref is attached to comes into view.
 *
 * This is the whole of the board's paging mechanism, and it is the same one in all three
 * directions. There is no control to press: a direction that has more mounts one of
 * these at the end of what is loaded, and reaching it is what asks for the next page.
 *
 * The observer is created against the viewport rather than against one of the board's
 * scroll containers, and does not need to know which one it is inside. An element
 * scrolled out of a cell is clipped by that cell whatever the observer's root is, so a
 * sentinel at the bottom of a cell nobody has scrolled is correctly reported as out of
 * view.
 *
 * Nothing is observed while `enabled` is false, so a direction with nothing left to load
 * costs no observer even if its element stays mounted.
 *
 * ## Asking once
 *
 * A reach is an arrival, not a state. Only the crossing from out of view to in view asks
 * for a page, so a sentinel that sits in view across any number of renders asks once, and
 * a sentinel that leaves and comes back asks again because that is a second arrival.
 * Suppressing the second arrival as well would be the wrong fix; there really is another
 * page, and the reader really did go and look for it.
 *
 * That leaves the arrival that is a duplicate rather than a second page: coming back
 * while the page the first arrival asked for has not yet been answered. The board fetches
 * nothing and holds no cursor, so what it goes on is the caller's own statement that a
 * page for this direction is in flight. While `isLoading` stands, an arrival is the same
 * question already outstanding and is not asked twice.
 *
 * Such an arrival is held rather than dropped, and handed over once that page is over. A
 * sentinel sitting in view has no second arrival to give, and a board too short to scroll
 * can produce no further crossing at all, so dropping the one arrival it had would strand
 * the direction on a page that failed. Exactly one is held, and only when an arrival
 * really did land while a page was out, because a direction re-armed on every settle
 * would ask again on its own and put a caller whose request keeps failing into a loop.
 *
 * The guard is deliberately not a record of having fetched. A flag set when a page is
 * asked for and cleared when one arrives latches on the page that never arrives, and the
 * direction that most needs to ask again is the one that can no longer ask at all.
 * Nothing here distinguishes a page that landed from a page that failed, because the two
 * mean the same thing to a sentinel: the question it asked is over, and the next arrival
 * is a new one. That is what makes a failed page retryable rather than terminal.
 *
 * A caller that never states `isLoading` keeps the part of the guard that spans a render
 * and gives up the part that spans a fetch. That is the contract working as intended
 * rather than a gap in it: the board derives no fact it was not told, and `isLoading` is
 * the only thing either side knows about a request being out.
 */
export function usePagingSentinel({onReached, enabled, isLoading}: PagingSentinelOptions): RefCallback<HTMLElement> {
  // The element is held as state rather than in a ref, because attaching it has to
  // re-run the effect below. A ref would leave the observer waiting on a mount React
  // never tells it about.
  const [element, setElement] = useState<HTMLElement | null>(null)

  // Where the sentinel was last seen, so an arrival can be told from a report about a
  // sentinel that never left. It is held here rather than inside the observer's closure
  // so that it outlives the observer: observing an element that is already in view
  // delivers a fresh intersecting record, and an observer rebuilt for any reason would
  // otherwise read that as an arrival and ask for the page again.
  const wasInViewRef = useRef(false)

  // An arrival that landed while a page was already in flight. It is held rather than
  // dropped, because a sentinel sitting in view has no second arrival to give: a board
  // too short to scroll can produce no further crossing, and dropping the one it had
  // would strand the direction on a page that failed.
  const pendingArrivalRef = useRef(false)

  // `onReached` closes over the address it pages and the caller's callback, so it is a
  // new function on most renders, and `isLoading` changes as the caller answers. Reading
  // both through an effect event keeps them out of the effect's dependencies, so the
  // observer survives a re-render rather than being disconnected and rebuilt. Rebuilding
  // it would re-observe an element already in view, which is the arrival this hook has
  // to tell apart from a real one.
  const reportArrival = useEffectEvent(() => {
    if (isLoading) {
      pendingArrivalRef.current = true
      return
    }

    pendingArrivalRef.current = false
    onReached()
  })

  // Hands over the one arrival that was held, once the page it was held behind is over.
  // Exactly one, and only if an arrival really did land while that page was out: a
  // direction re-armed on every settle would ask again on its own, and a caller whose
  // request keeps failing would be put in a loop by the guard meant to protect it.
  const reportHeldArrival = useEffectEvent(() => {
    if (isLoading || !pendingArrivalRef.current) return

    pendingArrivalRef.current = false
    onReached()
  })

  const ref = useCallback<RefCallback<HTMLElement>>(node => {
    setElement(node)
  }, [])

  useEffect(() => {
    if (element === null || !enabled) {
      // Nothing is watched, so nothing is in view as far as this hook is concerned. A
      // direction that stated it had nothing more and then found it does starts from out
      // of view, so its sentinel counts as arriving even though it never moved, and an
      // arrival held for a direction that has since said it is complete is dropped.
      wasInViewRef.current = false
      pendingArrivalRef.current = false

      return
    }

    const observer = new IntersectionObserver(
      entries => {
        // Records are an ordered queue of crossings, not a snapshot, so they are walked
        // in order. Reading only the last one would lose the arrival in a queue that
        // ends where it started, and a lost arrival is a direction that quietly stops
        // paging. At most one page is asked for per delivery, because a sentinel that
        // crossed in and out several times between two frames has still only arrived
        // somewhere once as far as the reader is concerned.
        let hasArrived = false

        for (const entry of entries) {
          if (entry.isIntersecting === wasInViewRef.current) continue

          wasInViewRef.current = entry.isIntersecting
          if (entry.isIntersecting) hasArrived = true
        }

        if (hasArrived) reportArrival()

        // Where the delivery ended is what decides whether a held arrival is still worth
        // handing over, and it is read after the arrival above rather than inside the
        // loop, because an arrival and a departure in one delivery ends out of view no
        // matter which order the guard sees them in. The reader who left before the page
        // they asked for settled is no longer at the end, so there is nothing to hand
        // them when it does.
        if (!wasInViewRef.current) pendingArrivalRef.current = false
      },
      {rootMargin: REACHED_MARGIN, scrollMargin: REACHED_MARGIN},
    )
    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [element, enabled])

  // Declared after the observer's effect so that a direction which stated it is complete
  // has already dropped whatever it was holding by the time this runs.
  useEffect(() => {
    reportHeldArrival()
  }, [isLoading])

  return ref
}

/** What a direction states about itself so its sentinel can ask for one page at a time. */
export type PagingSentinelOptions = {
  /** Reports that the end of what is loaded has been reached. */
  onReached: () => void
  /**
   * This direction has a further page and somebody is listening for it. Nothing is
   * observed while it is false, so a direction with nothing left to load costs no
   * observer even if its element stays mounted.
   */
  enabled: boolean
  /**
   * A page for **this** direction is in flight, as the caller states it. An arrival
   * while it stands is the question already outstanding rather than a new one.
   *
   * A caller that never states it still gets a sentinel that asks once per arrival; it
   * gives up only the part of the guard that spans a fetch.
   */
  isLoading: boolean
}

/**
 * Spread onto the element whose arrival in view asks for the next page, which is also
 * where the board's focus lands for that direction.
 *
 * More than a ref, because the two are the same box: the thing a reader scrolls to is the
 * thing that says a page is coming, so it carries the wording, and it carries the board's
 * one tab stop while the position is on it. See `usePagingStop`.
 */
export type PagingSentinelProps = HTMLAttributes<HTMLElement> & {
  ref: RefCallback<HTMLElement>
}
